import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ShowCard from '@/components/feature/ShowCard';
import { categories, featuredShows, shows, reviews } from '@/mocks/shows';

const heroSlides = [
  {
    id: 'show-2',
    title: '狂飙',
    subtitle: '2023年度现象级国产刑侦巨制',
    description: '一线刑警安欣与黑恶势力代表高启强长达二十年的正邪较量，张译、张颂文联袂演绎扫黑除恶史诗。',
    rating: 9.5,
    image: 'https://readdy.ai/api/search-image?query=Chinese%20crime%20thriller%20drama%20cinematic%20scene%2C%20dark%20and%20intense%20atmosphere%2C%20police%20interrogation%20room%2C%20dramatic%20lighting%2C%20moody%20noir%20style%2C%20warm%20amber%20and%20dark%20blue%20contrast%2C%20film%20grain%20texture&width=1600&height=700&seq=hero-kuangbiao&orientation=landscape',
  },
  {
    id: 'show-6',
    title: '繁花',
    subtitle: '王家卫首部电视剧 · 上海90年代风云史诗',
    description: '胡歌领衔主演，金宇澄同名小说改编，再现改革开放浪潮中的上海滩传奇故事。',
    rating: 9.3,
    image: 'https://readdy.ai/api/search-image?query=1990s%20Shanghai%20night%20street%20cinematic%20scene%2C%20neon%20lights%2C%20golden%20nostalgic%20atmosphere%2C%20elegant%20period%20drama%20aesthetic%2C%20warm%20amber%20and%20gold%20tones%2C%20film%20grain%2C%20vintage%20cinema%20style&width=1600&height=700&seq=hero-fanhua&orientation=landscape',
  },
  {
    id: 'show-1',
    title: '眼泪女王',
    subtitle: '2024最受欢迎韩剧 · 金秀贤金智媛主演',
    description: '百货女王与超市王子的婚姻危机到重新相爱的浪漫喜剧，笑中带泪的治愈之旅。',
    rating: 9.2,
    image: 'https://readdy.ai/api/search-image?query=Korean%20romantic%20drama%20cinematic%20scene%2C%20luxury%20fashion%2C%20elegant%20couple%20in%20beautiful%20garden%2C%20soft%20golden%20sunlight%2C%20romantic%20atmosphere%2C%20pastel%20color%20palette%2C%20dreamy%20cinematic%20style&width=1600&height=700&seq=hero-tears&orientation=landscape',
  },
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={`${slide.title} - 琪琪影院热播推荐`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 z-10">
              <div className="max-w-xl" key={heroSlides[currentSlide].id}>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-500/90 text-white text-xs font-medium mb-4">
                  <i className="ri-fire-line text-sm"></i>
                  <span>热播推荐</span>
                  <span className="mx-1">·</span>
                  <span>{heroSlides[currentSlide].rating} 分</span>
                </div>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3 leading-tight">
                  {heroSlides[currentSlide].title}
                </h1>
                <p className="text-sm md:text-base text-primary-200 font-medium mb-2">
                  {heroSlides[currentSlide].subtitle}
                </p>
                <p className="text-sm text-white/70 leading-relaxed mb-6 line-clamp-2">
                  {heroSlides[currentSlide].description}
                </p>
                <Link
                  to={`/article/${heroSlides[currentSlide].id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看详情
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  index === currentSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer border border-background-200 hover:border-primary-200"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-white transition-all mb-3">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-1">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-400 text-center">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Shows */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选高分好剧，每一部都值得看</p>
              </div>
              <Link
                to="/category/korean"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
              {featuredShows.slice(0, 6).map((show) => (
                <ShowCard key={show.id} show={show} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* All Shows by Category */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                热门电视剧
              </h2>
              <p className="text-sm text-foreground-500 mt-1">最新电视剧、热门电视剧全集推荐</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4" data-product-shop>
              {shows.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/category/korean"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-500 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap border border-background-200 hover:border-primary-200"
              >
                浏览全部剧集
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-10 md:py-14 bg-primary-500">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: '10,000+', label: '电视剧全集' },
                { value: '500万+', label: '活跃追剧用户' },
                { value: '99.8%', label: '高清流畅播放' },
                { value: '4.9', label: '用户评分' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-white">
                    {stat.value}
                  </div>
                  <div className="text-sm text-primary-200 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                用户好评
              </h2>
              <p className="text-sm text-foreground-500 mt-1">听听追剧达人们怎么说</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">关于琪琪影院的常见疑问解答</p>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: '琪琪影院有哪些类型的电视剧？',
                  a: '琪琪影院涵盖韩剧、国产剧、美剧、日剧等多种类型的热门电视剧与最新电视剧。无论是喜欢浪漫爱情、悬疑刑侦、还是治愈喜剧，都能在这里找到心仪的电视剧全集。',
                },
                {
                  q: '琪琪影院的剧集更新快吗？',
                  a: '我们保持与播出平台同步的更新速度，最新电视剧上线后会第一时间更新。热门电视剧的更新信息会在首页及时展示，确保您不会错过任何精彩内容。',
                },
                {
                  q: '琪琪影院的画质如何？',
                  a: '琪琪影院提供高清画质在线观看体验，支持多种清晰度选择。我们采用先进的视频技术，确保高清在线观看流畅不缓冲，为您带来最佳的追剧体验。',
                },
                {
                  q: '如何找到我想看的电视剧全集？',
                  a: '您可以通过首页的分类导航进入韩剧、国产剧、美剧、日剧专区，也可以使用搜索功能查找特定剧集。每个分类页面都支持按评分、年份、类型等条件筛选。',
                },
              ].map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-100 border border-background-200">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h4 className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</h4>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 md:py-14 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20interior%20with%20warm%20red%20velvet%20seats%2C%20soft%20ambient%20lighting%2C%20elegant%20atmosphere%2C%20empty%20theater%20with%20dramatic%20spotlight%2C%20warm%20golden%20tones%2C%20luxurious%20cinema%20aesthetic&width=1600&height=400&seq=cta-bg&orientation=landscape"
            alt="琪琪影院 - 追剧体验"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              开始你的追剧之旅
            </h2>
            <p className="text-sm text-white/70 mb-6">
              电视剧全集、最新电视剧、热门电视剧，尽在琪琪影院
            </p>
            <Link
              to="/category/korean"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-600 text-white rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              立即探索
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': '琪琪影院 - 电视剧全集最新电视剧热门电视剧',
          'description': '琪琪影院汇聚电视剧全集、最新电视剧、热门电视剧，韩剧国产剧全覆盖，高清在线免费看不缓冲，追剧首选平台。',
          'url': 'https://qiyicinema.com/',
          'isPartOf': {
            '@type': 'WebSite',
            'name': '琪琪影院',
            'url': 'https://qiyicinema.com',
          },
          'mainEntity': {
            '@type': 'FAQPage',
            'mainEntity': [
              {
                '@type': 'Question',
                'name': '琪琪影院有哪些类型的电视剧？',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': '琪琪影院涵盖韩剧、国产剧、美剧、日剧等多种类型的热门电视剧与最新电视剧。',
                },
              },
              {
                '@type': 'Question',
                'name': '琪琪影院的剧集更新快吗？',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': '我们保持与播出平台同步的更新速度，最新电视剧上线后会第一时间更新。',
                },
              },
              {
                '@type': 'Question',
                'name': '琪琪影院的画质如何？',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': '琪琪影院提供高清画质在线观看体验，支持多种清晰度选择。',
                },
              },
            ],
          },
        }),
      }} />
    </div>
  );
}